import { useEffect, useMemo, useState } from 'react';
import { IContact } from 'src/services/api/ServiceRequestDashboard/apiContracts';
import {
  useContactAPI,
  UseContactAPIReturn,
} from 'src/SharedCustomHooks/UseContactAPI/useContactAPI';
import {
  BulkUpdateAllowEmailChangeResult,
  useUserManagementAPI,
  UseUserManagementAPIReturn,
} from 'src/SharedCustomHooks/UseUserManagementAPI/useUserManagementAPI';
import useProperties from 'src/utils/CommonDataFetchingUtils/useProperties';
import { IProperty } from 'src/pages/Reporting/ReportingFormTypes';
import { UserRole } from 'src/SharedCustomHooks/UseSuiteActions/types';
import {
  ProjectFilter,
  RoleFilter,
  StagedEntry,
  StepId,
  UseEmailSelfServiceFormStateReturn,
  WIZARD_STEP_CONFIRMATION,
  useEmailSelfServiceFormState,
} from 'src/components/ContactsTable/EmailSelfServiceDialogue/CustomHooks/useEmailSelfServiceFormState/useEmailSelfServiceFormState';

/**
 * Orchestrator for the Email Self-Service dialogue. Composes:
 *
 *   - useContactAPI                — contact list fetch + fetch-side state
 *   - useEmailSelfServiceFormState — wizard UI + all derivations
 *   - useUserManagementAPI         — bulk PATCH of AllowEmailChange
 *
 * Intentionally thin. Derivations and wizard navigation live in the form-
 * state hook (pure helpers at the top of that file, unit-testable). This
 * layer only owns side effects: trigger fetch on open, apply the PATCH
 * buckets, and clear hook-level error/success state on reset.
 *
 * Mixed-batch apply (both allow and disallow buckets non-empty) currently
 * fires two PATCH calls sequentially via the hook, which produces two
 * success toasts back-to-back. Acceptable for v1 since uniform batches
 * are the common case; if this becomes a UX issue, suppress the hook-level
 * toast and emit a single aggregated toast here.
 */

export interface UseEmailSelfServiceOrchestratorArgs {
  isOpen: boolean;
  onSuccess?: (result: ApplyResult) => void;
}

export interface ApplyResult {
  ok: boolean;
  allowedCount: number;
  disallowedCount: number;
  totalUpdated: number;
}

export interface UseEmailSelfServiceOrchestratorReturn {
  currentStep: StepId;
  roleFilter: RoleFilter;
  projectFilter: ProjectFilter;
  searchQuery: string;
  selectedIds: Set<string>;

  setCurrentStep: (step: StepId) => void;
  setRoleFilter: (role: RoleFilter) => void;
  setProjectFilter: (projectId: ProjectFilter) => void;
  setSearchQuery: (query: string) => void;

  toggleSelection: (id: string) => void;
  setSelection: (ids: string[], selected: boolean) => void;
  clearSelection: () => void;

  setStagedValue: (id: string, allow: boolean) => void;
  removeFromStaging: (id: string) => void;
  bulkSetStaged: (allow: boolean) => void;

  availableRoles: string[];
  availableProjects: IProperty[];
  filteredContacts: IContact[];
  stagedEntries: StagedEntry[];
  actualChanges: StagedEntry[];
  unchangedContacts: StagedEntry[];
  nowAllowedEntries: StagedEntry[];
  nowDisallowedEntries: StagedEntry[];
  willAllowIds: string[];
  willDisallowIds: string[];

  contacts: IContact[];
  isLoadingContacts: boolean;
  contactsError: string | null;
  isSubmitting: boolean;
  error: string | null;
  successMessage: string | null;
  lastApplyResult: ApplyResult | null;

  canContinueFromStep1: boolean;
  canApplyFromStep2: boolean;

  stageSelectedAndContinue: () => void;
  goToStep: (step: StepId) => void;
  handleApply: () => Promise<ApplyResult>;
  handleStartOver: () => void;
  reset: () => void;
}

export const useEmailSelfServiceOrchestrator = ({
  isOpen,
  onSuccess,
}: UseEmailSelfServiceOrchestratorArgs): UseEmailSelfServiceOrchestratorReturn => {
  const contactAPI: UseContactAPIReturn = useContactAPI();
  const userManagementAPI: UseUserManagementAPIReturn = useUserManagementAPI();
  const { data: properties, fetchProperties } = useProperties();

  useEffect(() => {
    void fetchProperties();
  }, []);

  const availableProjects: IProperty[] = properties ?? [];

  const availableRoles: string[] = useMemo(() => {
    return userManagementAPI.allRoles
      .map((role: UserRole) => role.name)
      .sort((a: string, b: string) => a.localeCompare(b));
  }, [userManagementAPI.allRoles]);

  const formState: UseEmailSelfServiceFormStateReturn =
    useEmailSelfServiceFormState({ contacts: contactAPI.contacts });

  const [lastApplyResult, setLastApplyResult] = useState<ApplyResult | null>(
    null
  );

  useEffect(() => {
    if (isOpen) {
      void contactAPI.fetchContacts();
    }
  }, [isOpen]);

  const handleApply = async (): Promise<ApplyResult> => {
    if (!formState.canApplyFromStep2) {
      return {
        ok: false,
        allowedCount: 0,
        disallowedCount: 0,
        totalUpdated: 0,
      };
    }

    const emptyBucketResult: BulkUpdateAllowEmailChangeResult = {
      ok: true,
      updatedCount: 0,
    };

    let allowResult: BulkUpdateAllowEmailChangeResult = emptyBucketResult;

    if (formState.willAllowIds.length > 0) {
      allowResult = await userManagementAPI.bulkUpdateAllowEmailChange(
        formState.willAllowIds,
        true
      );
    }

    let disallowResult: BulkUpdateAllowEmailChangeResult = emptyBucketResult;

    if (formState.willDisallowIds.length > 0) {
      disallowResult = await userManagementAPI.bulkUpdateAllowEmailChange(
        formState.willDisallowIds,
        false
      );
    }

    const ok: boolean = allowResult.ok && disallowResult.ok;
    const allowedCount: number = allowResult.updatedCount;
    const disallowedCount: number = disallowResult.updatedCount;
    const totalUpdated: number = allowedCount + disallowedCount;

    const result: ApplyResult = {
      ok,
      allowedCount,
      disallowedCount,
      totalUpdated,
    };

    if (result.ok) {
      setLastApplyResult(result);
      formState.setCurrentStep(WIZARD_STEP_CONFIRMATION);
      onSuccess?.(result);
    }

    return result;
  };

  const handleStartOver = (): void => {
    formState.reset();
    userManagementAPI.clearError();
    userManagementAPI.clearSuccess();
    setLastApplyResult(null);
  };

  const reset = (): void => {
    formState.reset();
    userManagementAPI.clearError();
    userManagementAPI.clearSuccess();
    contactAPI.clearContactsError();
    setLastApplyResult(null);
  };

  return {
    currentStep: formState.currentStep,
    roleFilter: formState.roleFilter,
    projectFilter: formState.projectFilter,
    searchQuery: formState.searchQuery,
    selectedIds: formState.selectedIds,

    setCurrentStep: formState.setCurrentStep,
    setRoleFilter: formState.setRoleFilter,
    setProjectFilter: formState.setProjectFilter,
    setSearchQuery: formState.setSearchQuery,

    toggleSelection: formState.toggleSelection,
    setSelection: formState.setSelection,
    clearSelection: formState.clearSelection,

    setStagedValue: formState.setStagedValue,
    removeFromStaging: formState.removeFromStaging,
    bulkSetStaged: formState.bulkSetStaged,

    availableRoles,
    availableProjects,
    filteredContacts: formState.filteredContacts,
    stagedEntries: formState.stagedEntries,
    actualChanges: formState.actualChanges,
    unchangedContacts: formState.unchangedContacts,
    nowAllowedEntries: formState.nowAllowedEntries,
    nowDisallowedEntries: formState.nowDisallowedEntries,
    willAllowIds: formState.willAllowIds,
    willDisallowIds: formState.willDisallowIds,

    contacts: contactAPI.contacts,
    isLoadingContacts: contactAPI.isLoadingContacts,
    contactsError: contactAPI.contactsError,
    isSubmitting: userManagementAPI.isSubmitting,
    error: userManagementAPI.error,
    successMessage: userManagementAPI.successMessage,
    lastApplyResult,

    canContinueFromStep1: formState.canContinueFromStep1,
    canApplyFromStep2: formState.canApplyFromStep2,

    stageSelectedAndContinue: formState.stageSelectedAndContinue,
    goToStep: formState.goToStep,
    handleApply,
    handleStartOver,
    reset,
  };
};
